import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { RowSelectionState } from '@tanstack/react-table';
import { NETWORK_ERROR } from 'configs/messages';
import { DealCardFragments } from 'features/deals/cards/DealCard.fragments';
import { ListingCardFragments } from 'features/deals/cards/ListingCard';
import { includesFdicSaleOffering } from 'features/listings/fdicSale';
import { CSVLink } from 'react-csv';
import toast from 'react-hot-toast';
import { isNotNullOrUndefined } from 'utils/typeUtils';

interface SelectedListingId {
  parentIndex: number;
  children: number[];
}

import {
  ApolloError,
  gql,
  NetworkStatus,
  OperationVariables,
  QueryResult,
  useQuery,
} from '@apollo/client';

import { DealBlocker, DealStatus } from '__generated__/globalTypes';
import { GetDeals, GetSellerListings } from './__generated__/index';
import { activeListingsColumnDefinitions } from './ActiveListingsTable.config';
import { restrictedResizeColumnIds } from '../../../app-level/config/TableRestrictions';
import { FIELD_CONFIG } from '../../../configs/columns';
import { DataContext, DataContextActionType } from '../../../context/DataContext';
import { useColumnOrder } from '../../../hooks/useColumnOrder';
import { DataDisplayTable, DownloadDocumentButton } from '../../../ui-kit';
import { downloadExcelFromJson } from '../../../utils/excelUtils';

// Exported: ActiveListingsTable.config.tsx (imported below) shares this exact derivation and
// imports it from here rather than re-deriving it.
export type Listing = Extract<
  NonNullable<
    NonNullable<NonNullable<NonNullable<GetSellerListings>['user']>['company']>['offerings']
  >[number],
  { __typename: 'UserCompanyListing' }
>;
type GetSellerListings_user_company_offerings_UserCompanyListing_deals = NonNullable<
  NonNullable<
    Extract<
      NonNullable<
        NonNullable<NonNullable<NonNullable<GetSellerListings>['user']>['company']>['offerings']
      >[number],
      { __typename: 'UserCompanyListing' }
    >
  >['deals']
>[number];
type Deal = NonNullable<
  NonNullable<
    Extract<
      NonNullable<
        NonNullable<NonNullable<NonNullable<GetSellerListings>['user']>['company']>['offerings']
      >[number],
      { __typename: 'UserCompanyListing' }
    >
  >['deals']
>[number];
import {
  formatCentsToDollars,
  toPercentage,
} from '../../pages/marketplace/BiddingAndPricing/formatting';

type DealWithRole = Deal & { role: DealBlocker };

const ListingFragment = gql`
  fragment ListingFragment on Listing {
    __typename
    id
    loan_count
    wa_borrower_credit_score
    wa_coupon_bps
    wa_loan_age_months
    wa_ltv_bps
    wa_remaining_loan_terms_months
    sale_id
    ...ListingCardListing
  }
  ${ListingCardFragments.listing}
`;

const GET_SELLER_LISTINGS_QUERY = gql`
  query GetSellerListings {
    user {
      id
      company {
        id
        offerings {
          id
          ...ListingFragment
          ... on UserCompanyListing {
            is_public
            deals {
              id
              counterparty {
                ...DealCardCounterparty
              }
              ...DealCardDeal
            }
          }
        }
      }
    }
  }
  ${ListingFragment}
  ${DealCardFragments.counterparty}
  ${DealCardFragments.deal}
  ${ListingCardFragments.listing}
`;

const GET_DEALS_QUERY = gql`
  query GetDeals {
    user {
      id
      company {
        id
        buyingDeals: deals(role: BUYER) {
          id
          counterparty {
            ...DealCardCounterparty
          }
          ...DealCardDeal
          listing {
            id
            ...ListingFragment
          }
        }
        sellingDeals: deals(role: SELLER) {
          id
          counterparty {
            ...DealCardCounterparty
          }
          ...DealCardDeal
          listing {
            id
            ...ListingFragment
          }
        }
      }
    }
  }
  ${DealCardFragments.counterparty}
  ${DealCardFragments.deal}
  ${ListingFragment}
`;

export const ErrorMessage = (props: { err: ApolloError | undefined }): JSX.Element | null => {
  if (props.err) {
    if (process.env.NODE_ENV === 'development') {
      console.log('Error!', props.err);
    }
    return <div className="flex justify-center">{NETWORK_ERROR}</div>;
  }
  return null;
};

type Props = {
  gqlDeals: QueryResult<GetDeals, OperationVariables>;
};

const ActiveListings = (props: Props): JSX.Element => {
  const { state, dispatch } = useContext(DataContext);

  const { setColumnOrder } = useColumnOrder(
    'allTradesTable',
    state.globalTableOrderConfig.marketPlace.activeTrades.allTradesTable as string[],
  );
  const gql = useQuery<GetSellerListings>(GET_SELLER_LISTINGS_QUERY, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const { gqlDeals } = props;

  // invert the map of deals to listings to match the listing - subdata(deal) structure
  // TODO we likely want to only show rows, or always expand rows since buyer would only ever have one deal per listing
  const buyingListings = useMemo(() => {
    const listings: Record<string, Listing> = {};
    (gqlDeals.data?.user.company.buyingDeals ?? [])
      .filter((deal) => deal.state.status !== DealStatus.COMPLETE)
      .filter(isNotNullOrUndefined)
      .forEach(
        (deal) => {
          // deal.listing (from ListingFragment on the Listing interface) can
          // genuinely be either PublicListing or UserCompanyListing here --
          // a buyer's deal is on whatever the seller's listing is, not the
          // buyer's own. Every field this table reads off a buyingListings
          // row (id, providedName) is on the interface itself, visible on
          // both variants; `deals` is always attached locally right below,
          // never read off the real listing. So there's nothing to narrow
          // on -- assert past Listing's UserCompanyListing-only shape
          // rather than dropping real PublicListing-sourced deals (which a
          // __typename guard here would silently do) or stamping a false
          // __typename value onto the object (which would misrepresent a
          // PublicListing row's real discriminant to any future reader).
          const currentListing = listings[deal.listing.id];
          const currentDealsForListing = currentListing?.deals ?? [];
          listings[deal.listing.id] = {
            ...currentListing,
            ...deal.listing,
            deals: [
              ...currentDealsForListing,
              { ...deal, role: DealBlocker.BUYER } as DealWithRole,
            ],
          } as Listing;
        },
        {} as Record<string, Listing>,
      );
    return Object.values(listings).reduce(
      (previous, listing) => [...previous, { ...listing, subData: listing.deals }],
      new Array<Listing>(),
    );
  }, [gqlDeals.data]);

  const listingsToShow = useMemo(
    () =>
      gql.data?.user.company.offerings
        // Only UserCompanyListing carries is_public/deals at all -- a real
        // PublicListing has neither, so filtering on is_public after the old
        // blanket cast happened to work by accident (a missing property
        // reads as falsy undefined). Narrow for real instead of relying on
        // that.
        .filter((listing): listing is Listing => listing.__typename === 'UserCompanyListing')
        .filter((listing) => listing.is_public)
        .map((listing) => {
          return {
            ...listing,
            deals:
              listing.deals &&
              listing.deals
                .filter((deal) => DealStatus.COMPLETE !== deal.state.status)
                .map((deal) => ({ ...deal, role: DealBlocker.SELLER })),
            subData:
              listing.deals &&
              listing.deals
                .filter((deal) => DealStatus.COMPLETE !== deal.state.status)
                .map((deal) => ({ ...deal, role: DealBlocker.SELLER })),
          };
        }) ?? [],
    [gql.data],
  );

  const [selectedListings, setSelectedListings] = useState<RowSelectionState>({});

  useEffect(() => {
    dispatch({
      type: DataContextActionType.SET_LOADING,
      payload: gql.loading || gql.networkStatus === NetworkStatus.loading,
    });
  }, [gql.loading, gql.networkStatus]);

  const data = [...listingsToShow, ...buyingListings];
  const canExport = !includesFdicSaleOffering(data);

  const getColumns = () => {
    const columnMap = new Map(activeListingsColumnDefinitions.map((col) => [col.id, col]));
    return state.globalTableOrderConfig.marketPlace.activeTrades.allTradesTable
      .map((id) => columnMap.get(id))
      .filter((col) => col !== undefined);
  };

  const columns = useMemo(
    () => getColumns(),
    [state.globalTableOrderConfig.marketPlace.activeTrades.allTradesTable],
  );

  const groupSelectedIndexes = (selectionMap: Record<string, boolean>): SelectedListingId[] => {
    const grouped: Record<number, Set<number>> = {};
    for (const key of Object.keys(selectionMap)) {
      if (!selectionMap[key]) continue;

      if (key.includes('.')) {
        const [parentStr, childStr] = key.split('.');
        const parent = parseInt(parentStr, 10);
        const child = parseInt(childStr, 10);

        grouped[parent] ??= new Set();
        grouped[parent].add(child);
      } else {
        const parent = parseInt(key, 10);
        grouped[parent] ??= new Set();
      }
    }

    return Object.entries(grouped).map(([parentStr, childrenSet]) => ({
      parentIndex: Number(parentStr),
      children: Array.from(childrenSet),
    }));
  };

  const handleDownloadExcel = () => {
    //getting arranged ids Array
    const selectedListingsIds: SelectedListingId[] = groupSelectedIndexes(selectedListings);

    const selectedData: typeof data = [];

    if (selectedListingsIds.length === 0) {
      data.forEach((listing) => {
        const listingWithSubData = {
          ...listing,
          subData: listing.deals,
          deals: listing.deals,
        };
        selectedData.push(listingWithSubData);
      });
    } else {
      //By selected Ids Array collecting data
      selectedListingsIds.forEach((selectedObj) => {
        const parentIdx = selectedObj.parentIndex;

        const dealsSubData: GetSellerListings_user_company_offerings_UserCompanyListing_deals[] =
          [];

        //filtering children deals
        selectedObj.children.forEach((childIdx) => {
          dealsSubData.push(data[parentIdx].deals[childIdx]);
        });

        const selectedDataObj = {
          ...data[parentIdx],
          subData: dealsSubData,
          deals: dealsSubData,
        };

        selectedData.push(selectedDataObj);
      });
    }

    const downloadData: Array<Record<string, any>> = [];

    selectedData.forEach((selectedObj) => {
      downloadData.push({
        type: 'Pool',
        providedName: selectedObj.providedName,
        counterparty_name: '-',
        offered: 'true',
        state_label: '-',
        createdDate: FIELD_CONFIG.maturity_date.display(selectedObj.createdDate),
        assetClass: selectedObj.assetClass,
        upb: formatCentsToDollars(Number(selectedObj.upb)),
        loan_count: selectedObj.loan_count,
        wa_coupon_bps: toPercentage(Number(selectedObj.wa_coupon_bps)),
        wa_loan_age_months: selectedObj.wa_loan_age_months,
        wa_remaining_loan_terms_months: selectedObj.wa_remaining_loan_terms_months,
        wa_borrower_credit_score: selectedObj.wa_borrower_credit_score,
        wa_ltv_bps: toPercentage(Number(selectedObj.wa_ltv_bps)),
      });

      selectedObj.deals?.forEach((deal) => {
        downloadData.push({
          type: 'Deal',
          providedName: '-',
          counterparty_name: 'name' in deal.counterparty ? deal.counterparty.name : '',
          offered: 'true',
          state_label: deal.state.label,
          createdDate: FIELD_CONFIG.maturity_date.display(deal.createdDate),
          assetClass: deal.listing.assetClass,
          upb: '-',
          loan_count: '-',
          wa_coupon_bps: '-',
          wa_loan_age_months: '-',
          wa_remaining_loan_terms_months: '-',
          wa_borrower_credit_score: '-',
          wa_ltv_bps: '-',
        });
      });
    });

    if (!downloadData.length) {
      toast.error('No data available for download.');
      return;
    }

    downloadExcelFromJson(downloadData, 'Loan Data', 'Loan Data.xlsx');
  };

  return (
    <>
      <ErrorMessage err={gql.error} />
      <div className="flex justify-end">
        {canExport && (
          <DownloadDocumentButton
            onClick={handleDownloadExcel}
            fileType="excel"
            tooltipText={
              groupSelectedIndexes(selectedListings).length === 0
                ? 'Download all'
                : `Download ${groupSelectedIndexes(selectedListings).length} selected`
            }
            tooltipPosition="left"
            className="mr-10"
          />
        )}
      </div>
      <DataDisplayTable
        // @ts-expect-error - TS doesn't like the fact that we're omitting the listing column
        data={data}
        // @ts-expect-error - TS doesn't like the fact that we're omitting the listing column
        columns={columns}
        noDataMessage="Nothing to Show. Check the marketplace for loan pools, or publish your own."
        selection={{
          selected: selectedListings,
          onSelectionChange: setSelectedListings,
        }}
        stylingOptions={{
          alternateLightRows: true,
          subRowColor: 'black',
        }}
        // downloadCSV={true}
        isDraggable={true}
        resizeCfg={restrictedResizeColumnIds.marketPlace.activeTrades.allTradesTable}
        setColumnOrder={setColumnOrder}
        columnOrder={state.globalTableOrderConfig.marketPlace.activeTrades.allTradesTable}
        columnPinningLeft={['selection', 'icons']}
        isSelectionInfoIcon={false}
      />
    </>
  );
};

const InProgressOverview = (): JSX.Element => {
  const gqlDeals = useQuery<GetDeals>(GET_DEALS_QUERY, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  return (
    <div className="">
      <ActiveListings gqlDeals={gqlDeals} />
    </div>
  );
};

export { InProgressOverview };
